import { storage } from '@uhg-abyss/web/tools/storage';
import { jsonKey } from '@uhg-abyss/web/tools/jsonKey';

const createMemoryStore = () => {
  const MEMORY_STORE = {};

  const memoryStorage = {
    listItems: () => {
      return MEMORY_STORE;
    },
    getItem: (key) => {
      return MEMORY_STORE[key];
    },
    setItem: (key, value) => {
      MEMORY_STORE[key] = value;
    },
    removeItem: (key) => {
      delete MEMORY_STORE[key];
    },
  };

  const cache = {
    list: () => {
      return memoryStorage.listItems();
    },
    get: (key) => {
      return storage.getItem(memoryStorage, key);
    },
    set: (key, value, maxAge) => {
      return storage.setItem(memoryStorage, key, value, maxAge);
    },
    remove: (key) => {
      return memoryStorage.removeItem(key);
    },
  };

  return cache;
};

const actionCache = createMemoryStore();
const dispatchCache = createMemoryStore();

const cacheDefaults = {
  maxAge: 1000 * 60 * 15,
};

export const createAction = (action) => {
  const { meta, ...actionArgs } = action;

  if (!meta || !meta.async) {
    return (dispatch) => {
      return dispatch(action);
    };
  }

  return async (dispatch, getState) => {
    const { storeId } = getState();

    let cacheOptions;

    if (meta.cache !== false && action.cache !== false) {
      if (meta.cache !== false) {
        cacheOptions = {
          ...cacheDefaults,
          ...meta.cache,
        };
      } else if (action.cache !== false) {
        cacheOptions = {
          ...cacheDefaults,
          ...action.cache,
        };
      }
    }

    let deferredResolve;
    const useCache = cacheOptions && cacheOptions.maxAge > 0;

    if (useCache) {
      cacheOptions.token = jsonKey({
        ...action.payload,
        type: action.type,
        storeId,
      });

      const cachedAction = actionCache.get(cacheOptions.token);
      if (cachedAction) {
        return cachedAction.then((result) => {
          const { data, error } = result;

          if (data) {
            setTimeout(() => {
              if (meta && meta.onSuccess) {
                meta.onSuccess(data);
              }

              if (!dispatchCache.get(cacheOptions.token)) {
                dispatchCache.set(cacheOptions.token, true, 1000);

                dispatch({
                  type: `${action.type}_SUCCESS`,
                  payload: data,
                  meta: {
                    __abyss_async__: {
                      isLoading: false,
                      isSuccess: true,
                      isError: false,
                    },
                  },
                });
              }
            }, 100);
          } else if (error) {
            setTimeout(() => {
              if (meta && meta.onError) {
                meta.onError(error);
              }

              if (!dispatchCache.get(cacheOptions.token)) {
                dispatchCache.set(cacheOptions.token, true, 1000);

                dispatch({
                  type: `${action.type}_ERROR`,
                  payload: error,
                  meta: {
                    __abyss_async__: {
                      isLoading: false,
                      isError: true,
                      errorMessage: error.message || '',
                      errorCode: error.code || '',
                    },
                  },
                });
              }
            });
          }
        }, 100);
      }

      const actionPromise = new Promise((resolve) => {
        deferredResolve = resolve;
      });

      actionCache.set(cacheOptions.token, actionPromise, cacheOptions.maxAge);
    }

    dispatch({
      type: `${action.type}_STARTED`,
      meta: {
        __abyss_async__: {
          isLoading: true,
          isSuccess: false,
          isError: false,
          errorMessage: '',
          errorCode: '',
        },
      },
    });

    try {
      const data = await meta.async({ action: actionArgs, dispatch, getState });
      if (useCache) {
        dispatchCache.set(cacheOptions.token, true, 1000);
        deferredResolve({ data });
      }

      if (meta && meta.onSuccess) {
        meta.onSuccess(data);
      }

      dispatch({
        type: `${action.type}_SUCCESS`,
        payload: data,
        meta: {
          __abyss_async__: {
            isLoading: false,
            isSuccess: true,
          },
        },
      });
    } catch (e) {
      const error = e.data || e;
      if (useCache) {
        actionCache.remove(cacheOptions.token);
        dispatchCache.set(cacheOptions.token, true, 1000);
        deferredResolve({ error });
      }

      if (meta && meta.onError) {
        meta.onError(error);
      }

      dispatch({
        type: `${action.type}_ERROR`,
        payload: error,
        meta: {
          __abyss_async__: {
            isLoading: false,
            isError: true,
            errorMessage: error.message || '',
            errorCode: error.code || '',
          },
        },
      });
    }

    return null;
  };
};
